import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API, cn, formatDate, DEPARTMENT_COLORS, DEPARTMENT_LABELS } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package,
  Calendar,
  User,
  MapPin,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
  Upload,
  File,
  Trash2,
  Download,
  Eye,
} from "lucide-react";

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  
  // Document upload states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [uploadData, setUploadData] = useState({
    file: null,
    document_type: "customer_po",
    description: "",
  });
  const fileInputRef = useRef(null);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [categories, setCategories] = useState([]);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isMerchandizing = user?.role === "merchandizing_hod" || user?.role === "merchant";
  const canEditOrder = user?.role === "super_admin" || user?.role === "admin" || user?.role === "merchandizing_hod" || user?.role === "merchant";
  const isDraftOrPending = order?.status === "draft" || order?.status === "pending_approval";
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [editTemplates, setEditTemplates] = useState([]);
  const [editReason, setEditReason] = useState("");
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [addItemId, setAddItemId] = useState("");

  useEffect(() => {
    fetchOrder();
    fetchDocumentTypes();
    fetchCategories();
  }, [orderId]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setOrder(response.data);
    } catch (err) {
      console.error("Failed to fetch order:", err);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await axios.get(`${API}/document-types`, { withCredentials: true });
      setDocumentTypes(response.data);
    } catch (err) {
      console.error("Failed to fetch document types:", err);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadData.file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("document_type", uploadData.document_type);
      formData.append("description", uploadData.description);

      await axios.post(
        `${API}/orders/${orderId}/documents`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success("Document uploaded successfully");
      setShowUploadDialog(false);
      setUploadData({ file: null, document_type: "customer_po", description: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchOrder();
    } catch (err) {
      console.error("Failed to upload document:", err);
      toast.error(err.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    setDeletingDoc(documentId);
    try {
      await axios.delete(
        `${API}/orders/${orderId}/documents/${documentId}`,
        { withCredentials: true }
      );
      toast.success("Document deleted");
      fetchOrder();
    } catch (err) {
      console.error("Failed to delete document:", err);
      toast.error(err.response?.data?.detail || "Failed to delete document");
    } finally {
      setDeletingDoc(null);
    }
  };

  const handleDeleteOrder = async () => {
    setApproving(true);
    try {
      await axios.delete(`${API}/orders/${orderId}`, { withCredentials: true });
      toast.success("Order deleted successfully");
      navigate("/orders");
    } catch (err) {
      console.error("Failed to delete order:", err);
      toast.error(err.response?.data?.detail || "Failed to delete order");
    } finally {
      setApproving(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/orders/${orderId}/approve`,
        { approved: true },
        { withCredentials: true }
      );
      toast.success("Order approved! Tasks have been generated.");
      fetchOrder();
    } catch (err) {
      console.error("Failed to approve order:", err);
      toast.error(err.response?.data?.detail || "Failed to approve order");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setApproving(true);
    try {
      await axios.post(
        `${API}/orders/${orderId}/approve`,
        { approved: false, rejection_reason: rejectReason },
        { withCredentials: true }
      );
      toast.success("Order rejected");
      setShowRejectDialog(false);
      setRejectReason("");
      fetchOrder();
    } catch (err) {
      console.error("Failed to reject order:", err);
      toast.error(err.response?.data?.detail || "Failed to reject order");
    } finally {
      setApproving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/orders/${orderId}/submit-for-approval`,
        {},
        { withCredentials: true }
      );
      toast.success("Order submitted for approval");
      fetchOrder();
    } catch (err) {
      console.error("Failed to submit for approval:", err);
      toast.error(err.response?.data?.detail || "Failed to submit for approval");
    } finally {
      setApproving(false);
    }
  };

  const openEditDialog = async () => {
    if (!order) return;

    // Format delivery date for date input (YYYY-MM-DD)
    let deliveryDate = order.target_delivery_date || "";
    if (deliveryDate && deliveryDate.includes("T")) {
      deliveryDate = deliveryDate.split("T")[0];
    }

    setEditFields({
      notes: order.notes || "",
      po_number: order.po_number || "",
      shipping_address: order.shipping_address || "",
      billing_address: order.billing_address || "",
      payment_terms: order.payment_terms || "",
      target_delivery_date: deliveryDate,
    });

    // Clone current order items for editing
    setEditItems(
      (order.items || []).map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        quantity: item.quantity,
        image_url: item.image_url || null,
        category_id: item.category_id || null,
      }))
    );

    // Load available items and templates for adding
    try {
      const [itemsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/items`, { withCredentials: true }),
        axios.get(`${API}/task-templates`, { withCredentials: true }),
      ]);
      setAllItems(itemsRes.data);
      setAllTemplates(templatesRes.data);

      // Initialize edit templates from order's custom_task_templates or all templates
      if (order.custom_task_templates && order.custom_task_templates.length > 0) {
        setEditTemplates(
          order.custom_task_templates.map((t) => ({ ...t }))
        );
      } else {
        setEditTemplates(
          templatesRes.data.map((t) => ({
            task_name: t.task_name,
            department: t.department,
            deadline_days: t.deadline_days,
            is_enabled: true,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to load edit data:", err);
      setAllItems([]);
      setAllTemplates([]);
      setEditTemplates([]);
    }

    setEditReason("");
    setAddItemId("");
    setShowEditDialog(true);
  };

  const handleSubmitEditRequest = async () => {
    const changes = {};
    if (order) {
      // Check basic fields
      let deliveryDate = order.target_delivery_date || "";
      if (deliveryDate && deliveryDate.includes("T")) {
        deliveryDate = deliveryDate.split("T")[0];
      }
      const originalFields = {
        notes: order.notes || "",
        po_number: order.po_number || "",
        shipping_address: order.shipping_address || "",
        billing_address: order.billing_address || "",
        payment_terms: order.payment_terms || "",
        target_delivery_date: deliveryDate,
      };
      for (const [key, value] of Object.entries(editFields)) {
        if (value !== originalFields[key]) {
          changes[key] = value;
        }
      }

      // Check items changes
      const originalItemIds = (order.items || []).map((i) => i.item_id).sort().join(",");
      const editItemIds = editItems.map((i) => i.item_id).sort().join(",");
      const originalQtys = (order.items || []).map((i) => `${i.item_id}:${i.quantity}`).sort().join(",");
      const editQtys = editItems.map((i) => `${i.item_id}:${i.quantity}`).sort().join(",");
      if (originalItemIds !== editItemIds || originalQtys !== editQtys) {
        changes.items = editItems.map((i) => ({
          item_id: i.item_id,
          item_name: i.item_name,
          quantity: i.quantity,
        }));
      }

      // Check template changes
      const origTemplates = JSON.stringify(order.custom_task_templates || []);
      const editTmplCleaned = editTemplates.map((t) => ({
        task_name: t.task_name,
        department: t.department,
        deadline_days: t.deadline_days,
        is_enabled: t.is_enabled,
        notes: t.notes || null,
      }));
      const newTemplates = JSON.stringify(editTmplCleaned);
      if (origTemplates !== newTemplates) {
        changes.custom_task_templates = editTmplCleaned;
      }
    }

    if (Object.keys(changes).length === 0) {
      toast.error("No changes made");
      return;
    }

    setSubmittingEdit(true);
    try {
      const isDraftPending = order.status === "draft" || order.status === "pending_approval";
      if (isDraftPending) {
        await axios.put(
          `${API}/orders/${orderId}/edit`,
          changes,
          { withCredentials: true }
        );
        toast.success("Order updated successfully");
        fetchOrder();
      } else {
        await axios.post(
          `${API}/order-edit-requests`,
          { order_id: orderId, changes, reason: editReason },
          { withCredentials: true }
        );
        toast.success("Edit request submitted for Super Admin approval");
      }
      setShowEditDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit edit");
    } finally {
      setSubmittingEdit(false);
    }
  };

  if (loading) {
    return <OrderDetailSkeleton />;
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Order not found</p>
        <Button variant="outline" onClick={() => navigate("/orders")} className="mt-4">
          Back to Orders
        </Button>
      </div>
    );
  }

  const isPendingApproval = order.status === "pending_approval";
  const isDraft = order.status === "draft";
  const completionRate = order.total_tasks > 0 
    ? Math.round((order.completed_tasks / order.total_tasks) * 100) 
    : 0;

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/orders")}
            className="rounded-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl tracking-tight">{order.order_number}</h1>
              <Badge
                variant="secondary"
                className={cn(
                  "font-mono text-xs uppercase",
                  order.status === "active" && "bg-emerald-50 text-emerald-700",
                  order.status === "pending_approval" && "bg-violet-50 text-violet-700",
                  order.status === "draft" && "bg-slate-50 text-slate-700",
                  order.status === "completed" && "bg-blue-50 text-blue-700"
                )}
              >
                {order.status === "pending_approval" ? "Pending Approval" : order.status}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">{order.customer_name}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {isDraft && (
            <Button
              onClick={handleSubmitForApproval}
              disabled={approving}
              className="rounded-sm"
              data-testid="submit-for-approval-btn"
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit for Approval
            </Button>
          )}

          {isPendingApproval && isAdmin && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowRejectDialog(true)}
                disabled={approving}
                className="rounded-sm text-destructive hover:text-destructive"
                data-testid="reject-order-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={approving}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="approve-order-btn"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve Order
              </Button>
            </>
          )}

          {/* Direct Edit - for draft/pending orders */}
          {canEditOrder && isDraftOrPending && (
            <Button
              variant="outline"
              onClick={openEditDialog}
              className="rounded-sm"
              data-testid="direct-edit-order-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Edit Order
            </Button>
          )}

          {/* Request Edit - for active orders, needs super admin approval */}
          {canEditOrder && order.status === "active" && (
            <Button
              variant="outline"
              onClick={openEditDialog}
              className="rounded-sm"
              data-testid="edit-order-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Request Edit
            </Button>
          )}

          {/* Admin Delete Button */}
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-sm text-destructive hover:text-destructive"
                  data-testid="delete-order-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Order
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Order</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete order "{order.order_number}"?
                    <br /><br />
                    This will permanently delete the order and all associated tasks and documents.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={handleDeleteOrder}
                  >
                    Delete Order
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Rejection Reason Alert */}
      {order.rejection_reason && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Order Rejected</p>
                <p className="text-sm text-muted-foreground mt-1">{order.rejection_reason}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Info */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl">Order Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Order Date</p>
                    <p className="font-medium">{formatDate(order.order_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Target Delivery</p>
                    <p className="font-medium">{formatDate(order.target_delivery_date)}</p>
                  </div>
                </div>
                {order.po_number && (
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">PO Number</p>
                      <p className="font-medium font-mono">{order.po_number}</p>
                    </div>
                  </div>
                )}
                {order.payment_terms && (
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Payment Terms</p>
                      <p className="font-medium">{order.payment_terms}</p>
                    </div>
                  </div>
                )}
              </div>

              {(order.shipping_address || order.billing_address) && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    {order.shipping_address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs text-muted-foreground">Shipping Address</p>
                          <p className="text-sm whitespace-pre-wrap">{order.shipping_address}</p>
                        </div>
                      </div>
                    )}
                    {order.billing_address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs text-muted-foreground">Billing Address</p>
                          <p className="text-sm whitespace-pre-wrap">{order.billing_address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {order.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{order.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl">Order Items</CardTitle>
              <CardDescription>{order.items?.length || 0} items</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center overflow-hidden">
                            {item.image_url ? (
                              <img src={item.image_url.startsWith("/api") ? `${API.replace("/api", "")}${item.image_url}` : item.image_url} alt={item.item_name} className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{item.item_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const catName = item.category_id
                            ? categories.find(c => c.category_id === item.category_id)?.name
                            : null;
                          return catName ? (
                            <Badge variant="outline" className="text-xs">{catName}</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="font-mono">{item.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Task Templates Section - shown for draft/pending orders */}
          {isDraftOrPending && order.custom_task_templates && order.custom_task_templates.length > 0 && (
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Assigned Task Templates</CardTitle>
                <CardDescription>
                  These tasks will be created upon order approval. Edit the order to modify.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {["merchandizing", "prepress", "production", "logistics"].map(dept => {
                    const deptTemplates = order.custom_task_templates.filter(t => t.department === dept && t.is_enabled);
                    if (deptTemplates.length === 0) return null;
                    return (
                      <div key={dept}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{dept}</p>
                        <div className="space-y-1">
                          {deptTemplates.map((t, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-muted/50 rounded-sm text-sm">
                              <span>{t.task_name}</span>
                              <Badge variant="outline" className="text-xs font-mono">{t.deadline_days}d</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents Section */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl">Documents</CardTitle>
                  <CardDescription>
                    {order.documents?.length || 0} document(s) attached
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => setShowUploadDialog(true)}
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(!order.documents || order.documents.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No documents uploaded</p>
                  <p className="text-xs">Upload POs, invoices, or other relevant files</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {order.documents.map((doc) => (
                    <div
                      key={doc.document_id}
                      className="flex items-center justify-between p-3 rounded-sm border bg-muted/30"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-background rounded-sm">
                          <File className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{doc.filename}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type}
                            </Badge>
                            <span>•</span>
                            <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                            <span>•</span>
                            <span>{formatDate(doc.uploaded_at)}</span>
                          </div>
                          {doc.description && (
                            <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => window.open(`${API.replace("/api", "")}${doc.url}`, "_blank")}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          asChild
                        >
                          <a href={`${API.replace("/api", "")}${doc.url}`} download={doc.filename}>
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                            disabled={deletingDoc === doc.document_id}
                          >
                            {deletingDoc === doc.document_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress Card */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl">Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <p className="text-4xl font-heading">{completionRate}%</p>
                <p className="text-sm text-muted-foreground">Complete</p>
              </div>
              <Progress value={completionRate} className="h-2" />
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-heading">{order.completed_tasks || 0}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
                <div>
                  <p className="text-2xl font-heading">{order.total_tasks || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Task Summary by Department */}
          {order.tasks && order.tasks.length > 0 && (
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Tasks by Department</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(
                    order.tasks.reduce((acc, task) => {
                      if (!acc[task.department]) {
                        acc[task.department] = { total: 0, completed: 0 };
                      }
                      acc[task.department].total++;
                      if (task.status === "completed") {
                        acc[task.department].completed++;
                      }
                      return acc;
                    }, {})
                  ).map(([dept, stats]) => {
                    const colors = DEPARTMENT_COLORS[dept] || DEPARTMENT_COLORS.merchandizing;
                    const rate = Math.round((stats.completed / stats.total) * 100);
                    return (
                      <div key={dept} className={cn("p-3 rounded-sm border-l-4", colors.border)}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium capitalize">
                            {DEPARTMENT_LABELS[dept] || dept}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground">
                            {stats.completed}/{stats.total}
                          </span>
                        </div>
                        <Progress value={rate} className="h-1" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Created/Updated Info */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardContent className="p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span>Created by {order.created_by_name || order.created_by}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>Created {formatDate(order.created_at)}</span>
              </div>
              {order.approved_by && (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Approved by {order.approved_by_name || order.approved_by}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Order</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this order. The creator will be notified.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={approving}
              data-testid="confirm-reject-btn"
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reject Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for this order (PO, Invoice, Shipping docs, etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select
                value={uploadData.document_type}
                onValueChange={(value) => setUploadData(prev => ({ ...prev, document_type: value }))}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input
                type="file"
                ref={fileInputRef}
                onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                className="rounded-sm"
              />
              <p className="text-xs text-muted-foreground">
                Allowed: PDF, Word, Excel, Images (max 10MB)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                placeholder="Brief description of the document..."
                value={uploadData.description}
                onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} disabled={uploading || !uploadData.file}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{isDraftOrPending ? "Edit Order" : "Request Order Edit"}</DialogTitle>
            <DialogDescription>
              {isDraftOrPending
                ? "Modify order details, items, delivery date, and task templates."
                : "Changes will be submitted for Super Admin approval."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto flex-1 pr-2">

            {/* Delivery Date */}
            <div className="space-y-2">
              <Label className="font-semibold">Target Delivery Date</Label>
              <Input
                type="date"
                value={editFields.target_delivery_date || ""}
                onChange={(e) => setEditFields((p) => ({ ...p, target_delivery_date: e.target.value }))}
                className="rounded-sm max-w-xs"
                data-testid="edit-delivery-date"
              />
            </div>

            <Separator />

            {/* Order Items */}
            <div className="space-y-3">
              <Label className="font-semibold">Order Items</Label>
              {editItems.length > 0 ? (
                <div className="border border-border rounded-sm divide-y divide-border">
                  {editItems.map((item, idx) => {
                    const catName = item.category_id
                      ? categories.find((c) => c.category_id === item.category_id)?.name
                      : null;
                    return (
                      <div key={item.item_id} className="flex items-center gap-3 p-3" data-testid={`edit-item-row-${item.item_id}`}>
                        <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                          {item.image_url ? (
                            <img src={item.image_url.startsWith("/api") ? `${API.replace("/api", "")}${item.image_url}` : item.image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.item_name}</p>
                          {catName && <span className="text-xs text-muted-foreground">{catName}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground">Qty:</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const newQty = parseInt(e.target.value) || 1;
                              setEditItems((prev) => prev.map((i, iIdx) => iIdx === idx ? { ...i, quantity: newQty } : i));
                            }}
                            className="w-24 rounded-sm h-8"
                            data-testid={`edit-item-qty-${item.item_id}`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                          onClick={() => setEditItems((prev) => prev.filter((_, iIdx) => iIdx !== idx))}
                          data-testid={`edit-remove-item-${item.item_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-sm">No items</p>
              )}

              {/* Add Item */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Select value={addItemId} onValueChange={setAddItemId}>
                    <SelectTrigger className="rounded-sm" data-testid="edit-add-item-select">
                      <SelectValue placeholder="Select item to add..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allItems
                        .filter((ai) => !editItems.some((ei) => ei.item_id === ai.item_id))
                        .map((ai) => (
                          <SelectItem key={ai.item_id} value={ai.item_id}>
                            {ai.name} ({ai.sku})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm"
                  disabled={!addItemId}
                  data-testid="edit-add-item-btn"
                  onClick={() => {
                    const ai = allItems.find((i) => i.item_id === addItemId);
                    if (ai) {
                      setEditItems((prev) => [
                        ...prev,
                        { item_id: ai.item_id, item_name: ai.name, quantity: 1, image_url: ai.image_url, category_id: ai.category_id },
                      ]);
                      setAddItemId("");
                    }
                  }}
                >
                  Add Item
                </Button>
              </div>
            </div>

            <Separator />

            {/* Task Templates */}
            <div className="space-y-3">
              <Label className="font-semibold">Task Templates</Label>
              <p className="text-xs text-muted-foreground">Toggle tasks on/off and adjust deadline days. Only enabled tasks will be created on approval.</p>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {["merchandizing", "prepress", "production", "logistics"].map((dept) => {
                  const deptTemplates = editTemplates.filter((t) => t.department === dept);
                  if (deptTemplates.length === 0) return null;
                  return (
                    <div key={dept}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">{dept}</p>
                      <div className="space-y-1">
                        {deptTemplates.map((t) => {
                          const globalIdx = editTemplates.indexOf(t);
                          return (
                            <div
                              key={globalIdx}
                              className={cn(
                                "flex items-center gap-3 py-1.5 px-3 rounded-sm text-sm",
                                t.is_enabled ? "bg-muted/50" : "bg-muted/20 opacity-60"
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={t.is_enabled}
                                onChange={(e) => {
                                  setEditTemplates((prev) =>
                                    prev.map((tmpl, tIdx) =>
                                      tIdx === globalIdx ? { ...tmpl, is_enabled: e.target.checked } : tmpl
                                    )
                                  );
                                }}
                                className="rounded"
                                data-testid={`edit-template-toggle-${globalIdx}`}
                              />
                              <span className="flex-1 truncate">{t.task_name}</span>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="1"
                                  value={t.deadline_days}
                                  onChange={(e) => {
                                    const newDays = parseInt(e.target.value) || t.deadline_days;
                                    setEditTemplates((prev) =>
                                      prev.map((tmpl, tIdx) =>
                                        tIdx === globalIdx ? { ...tmpl, deadline_days: newDays } : tmpl
                                      )
                                    );
                                  }}
                                  className="w-16 rounded-sm h-7 text-xs"
                                  data-testid={`edit-template-days-${globalIdx}`}
                                />
                                <span className="text-xs text-muted-foreground">days</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Other Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PO Number</Label>
                <Input
                  value={editFields.po_number || ""}
                  onChange={(e) => setEditFields((p) => ({ ...p, po_number: e.target.value }))}
                  className="rounded-sm"
                  data-testid="edit-po-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Input
                  value={editFields.payment_terms || ""}
                  onChange={(e) => setEditFields((p) => ({ ...p, payment_terms: e.target.value }))}
                  className="rounded-sm"
                  data-testid="edit-payment-terms"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={editFields.notes || ""}
                  onChange={(e) => setEditFields((p) => ({ ...p, notes: e.target.value }))}
                  className="rounded-sm"
                  rows={2}
                  data-testid="edit-notes"
                />
              </div>
              <div className="space-y-2">
                <Label>Shipping Address</Label>
                <Textarea
                  value={editFields.shipping_address || ""}
                  onChange={(e) => setEditFields((p) => ({ ...p, shipping_address: e.target.value }))}
                  className="rounded-sm"
                  rows={2}
                  data-testid="edit-shipping-address"
                />
              </div>
              <div className="space-y-2">
                <Label>Billing Address</Label>
                <Textarea
                  value={editFields.billing_address || ""}
                  onChange={(e) => setEditFields((p) => ({ ...p, billing_address: e.target.value }))}
                  className="rounded-sm"
                  rows={2}
                  data-testid="edit-billing-address"
                />
              </div>
            </div>

            {/* Reason (for active order edit requests only) */}
            {!isDraftOrPending && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label>Reason for Edit</Label>
                  <Textarea
                    placeholder="Why is this edit needed?"
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    className="rounded-sm"
                    data-testid="edit-reason"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitEditRequest}
              disabled={submittingEdit}
              className="rounded-sm"
              data-testid="submit-edit-request-btn"
            >
              {submittingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isDraftOrPending ? "Save Changes" : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const OrderDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-10 w-10" />
      <div>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32 mt-2" />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 space-y-6">
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
      <div className="space-y-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-48" />
      </div>
    </div>
  </div>
);
